export type DebiComponentType = 'DPSRRS' | 'OFRS' | 'OFES' | 'RPRS' | 'VAS'

export type DebiComponentTotals = Partial<Record<DebiComponentType, {
  answer: number;
  total: number;
}>>

export type DebiComponentBreakdown = {
  type: DebiComponentType;
  answer: number | null;
  total: number | null;
  denominator: number | null;
  denominatorSource: 'total' | 'OFRS.answer' | 'RPRS.answer';
  rate: number | null;
  cap: number;
  cappedRate: number | null;
  weight: number;
  contribution: number | null;
}

export type DebiBehaviorBreakdown = {
  score: number | null;
  componentsFound: number;
  components: DebiComponentBreakdown[];
}

export const DEBI_COMPONENT_TYPES: DebiComponentType[] = [
  'DPSRRS',
  'OFRS',
  'OFES',
  'RPRS',
  'VAS',
]

export const DEBI_COMPONENT_WEIGHTS: Record<DebiComponentType, number> = {
  DPSRRS: 0.34,
  OFRS: 0.22,
  OFES: 0.11,
  RPRS: 0.22,
  VAS: 0.11,
}

export const DEBI_COMPONENT_MAX: Record<DebiComponentType, number> = {
  DPSRRS: 1,
  OFRS: 0.8,
  OFES: 1,
  RPRS: 1,
  VAS: 0.8,
}

function finiteNumber(value: any): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return isFinite(parsed) ? parsed : null
}

function roundDebiNumber(value: number): number {
  return Math.round(value * 10) / 10
}

function getComponentDenominator(
  type: DebiComponentType,
  components: DebiComponentTotals
): { denominator: number | null; source: 'total' | 'OFRS.answer' | 'RPRS.answer' } {
  const component = components[type]

  if (type === 'OFES') {
    return {
      denominator: components.OFRS ? finiteNumber(components.OFRS.answer) : null,
      source: 'OFRS.answer',
    }
  }

  if (type === 'VAS') {
    return {
      denominator: components.RPRS ? finiteNumber(components.RPRS.answer) : null,
      source: 'RPRS.answer',
    }
  }

  return {
    denominator: component ? finiteNumber(component.total) : null,
    source: 'total',
  }
}

function getComponentRawScore(type: DebiComponentType, components: DebiComponentTotals): number | null {
  const component = components[type]
  if (!component) return null

  const answer = finiteNumber(component.answer)
  if (answer === null) return null

  const denominator = getComponentDenominator(type, components).denominator
  return denominator && denominator > 0 ? answer / denominator : null
}

export function calculateDebiBehaviorBreakdown(components: DebiComponentTotals): DebiBehaviorBreakdown {
  let weightedScore = 0
  let componentsFound = 0
  const breakdown: DebiComponentBreakdown[] = []

  DEBI_COMPONENT_TYPES.forEach((type) => {
    const component = components[type]
    const answer = component ? finiteNumber(component.answer) : null
    const total = component ? finiteNumber(component.total) : null
    const denominatorInfo = getComponentDenominator(type, components)
    const rawScore = getComponentRawScore(type, components)
    const cappedScore = rawScore === null
      ? null
      : Math.min(rawScore / DEBI_COMPONENT_MAX[type], 1)

    if (cappedScore !== null) {
      weightedScore += cappedScore * DEBI_COMPONENT_WEIGHTS[type]
      componentsFound++
    }

    breakdown.push({
      type,
      answer,
      total,
      denominator: denominatorInfo.denominator,
      denominatorSource: denominatorInfo.source,
      rate: rawScore === null ? null : roundDebiNumber(rawScore * 100),
      cap: roundDebiNumber(DEBI_COMPONENT_MAX[type] * 100),
      cappedRate: cappedScore === null ? null : roundDebiNumber(cappedScore * 100),
      weight: roundDebiNumber(DEBI_COMPONENT_WEIGHTS[type] * 100),
      contribution: cappedScore === null ? null : roundDebiNumber(cappedScore * DEBI_COMPONENT_WEIGHTS[type] * 100),
    })
  })

  return {
    score: componentsFound === 0 ? null : roundDebiNumber(weightedScore * 100),
    componentsFound,
    components: breakdown,
  }
}

export function calculateDebiBehaviorScore(components: DebiComponentTotals): number | null {
  return calculateDebiBehaviorBreakdown(components).score
}

export function addDebiComponentValue(
  components: DebiComponentTotals,
  type: string,
  answerValue: any,
  totalValue: any
): void {
  if (DEBI_COMPONENT_TYPES.indexOf(type as DebiComponentType) < 0) return

  const answer = finiteNumber(answerValue)
  const total = finiteNumber(totalValue)
  if (answer === null && total === null) return

  const key = type as DebiComponentType
  if (!components[key]) {
    components[key] = { answer: 0, total: 0 }
  }

  components[key]!.answer += answer || 0
  components[key]!.total += total || 0
}
